import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppointmentsList from "./ClientComponents/AppointmentsList";
import ClientProfile from "./ClientComponents/ClientProfile";
import FavoriteMasters from "./ClientComponents/FavoriteMasters";
import SettingsPanel from "./ClientComponents/SettingsPanel";
import InstallSlider from "./Installslider/Installslider";
import './ClientDashboard.css';
import {
  MdCalendarMonth, MdPerson, MdFavorite, MdSettings,
} from "react-icons/md";
import { getServerUrl } from "../../../Utils/serverSelector";
import { useDispatch } from "react-redux";
import { loginMaster } from "../../../redux/slices/masterSessionSlice";
import LanguageScreen from "../../../Components/Languages/LanguageScreen/LanguageScreen";

const TABS = [
  { id: "appointments", Icon: MdCalendarMonth, labelKey: "my_appointments", labelRu: "Записи" },
  { id: "profile", Icon: MdPerson, labelKey: "my_profile", labelRu: "Профиль" },
  { id: "favorites", Icon: MdFavorite, labelKey: "favorite_masters", labelRu: "Мастера" },
  { id: "settings", Icon: MdSettings, labelKey: "settings", labelRu: "Настройки" },
];

/* ─── тексты экрана «войдите заново» ───────────────────── */
const RELOGIN_TX = {
  title: { ru: "Войдите заново", he: "התחבר מחדש", en: "Please log in again" },
  message: {
    ru: "В целях безопасности доступ к личному кабинету требует входа. Войдите через WhatsApp — это займёт пару секунд, и все ваши записи снова будут здесь.",
    he: "מטעמי אבטחה, גישה לאזור האישי דורשת התחברות. היכנס דרך WhatsApp — זה ייקח כמה שניות וכל התורים שלך יחזרו לכאן.",
    en: "For your security, access to your account requires signing in. Log in via WhatsApp — it takes a couple of seconds and all your appointments will be back here.",
  },
  login: { ru: "Войти через WhatsApp", he: "כניסה דרך WhatsApp", en: "Log in via WhatsApp" },
  reload: { ru: "Обновить страницу", he: "רענון הדף", en: "Reload page" },
};
const rtx = (k, lang) => RELOGIN_TX[k]?.[lang] || RELOGIN_TX[k]?.ru || "";

/* ─── PWA хук ───────────────────────────────────────────── */
function usePwaInstall() {
  const [prompt, setPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  useState(() => {
    if (isStandalone) { setIsInstalled(true); return; }
    if (isIos) return;
    const handler = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  });

  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") { setIsInstalled(true); return true; }
    return false;
  };

  return {
    canInstall: !!prompt && !isInstalled,
    isIos: isIos && !isStandalone,
    install,
  };
}

/* ─── Главный компонент ─────────────────────────────────── */
export default function ClientDashboard() {
  const [activeTab, setActiveTab] = useState("appointments");
  const [showBanner, setShowBanner] = useState(
    () => localStorage.getItem('install_banner_dismissed') !== 'true'
  );

  const { t, i18n } = useTranslation();
  const lang = i18n?.language?.slice(0, 2) || "ru";
  const isRTL = lang === "he";
  const navigate = useNavigate();

  const sessionAccount = useSelector(s => s.userSession?.currentAccount);
  const currentUser = useSelector(s => s.users?.currentUser);
  const account = sessionAccount || currentUser;
  const photoPath = useSelector(s => s.photouser?.photoPath);

  // ✅ Берём мастер-аккаунт из Redux — он уже загружен в AppInitializer
  const masterLink = useSelector(s => s.masterSession?.currentAccount?.masterLink) || null;

  const dispatch = useDispatch();

  const { canInstall, isIos, install } = usePwaInstall();

  const showInstallBanner = showBanner && (canInstall || isIos);

  // ── Защита от мелькания: даём сессии ~800мс на загрузку,
  //    и только если данных так и нет — показываем экран входа ──
  const sessionLoading = useSelector(s => s.userSession?.loading);

  const [sessionChecked, setSessionChecked] = useState(false);
  useEffect(() => {
    if (account) { setSessionChecked(true); return; }
    if (sessionLoading) return; // запрос сессии ещё идёт — не показываем «войдите заново»
    const tmr = setTimeout(() => setSessionChecked(true), 800);
    return () => clearTimeout(tmr);
  }, [account, sessionLoading]);

  // Переход на дашборд мастера
  const handleSwitchToMaster = async () => {
    if (!masterLink) return;
    try {
      const phone = account?.phone;
      if (phone) {
        await dispatch(loginMaster(phone)).unwrap();
      }
      const linkKey = masterLink.replace(/^master\//, "");
      navigate(`/${lang}/master/${linkKey}/master-dashboard`);
    } catch (err) {
      console.error("[switchToMaster]", err);
      const linkKey = masterLink.replace(/^master\//, "");
      navigate(`/${lang}/master/${linkKey}/master-dashboard`);
    }
  };

  const dismissBanner = () => {
    localStorage.setItem('install_banner_dismissed', 'true');
    setShowBanner(false);
  };

  const goRelogin = () => navigate(`/${lang}/login`);

  const initials = account
    ? `${account.userName?.[0] || account.firstName?.[0] || ""}${account.userLastName?.[0] || account.lastName?.[0] || ""}`.toUpperCase()
    : "?";

  const fullName = account
    ? `${account.userName || account.firstName || ""} ${account.userLastName || account.lastName || ""}`.trim()
    : "";

  const photoUrl = photoPath ? `${getServerUrl()}${photoPath}` : null;

  const activeTabObj = TABS.find(tab => tab.id === activeTab);
  const activeLabel = activeTabObj ? (t(activeTabObj.labelKey) || activeTabObj.labelRu) : "";

  // ══════════════════════════════════════════════════════════
  // НЕТ СЕССИИ/ДАННЫХ
  // ══════════════════════════════════════════════════════════
  if (!account) {
    // пока проверяем сессию — короткий лоадер (чтобы экран не мелькал)
    if (!sessionChecked) {
      return (
        <div className="cd-root" dir={isRTL ? "rtl" : "ltr"}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <div style={{ fontSize: 30 }}>⏳</div>
        </div>
      );
    }

    // данных нет → экран «войдите заново» (безопасность)
    return (
      <div className="cd-root" dir={isRTL ? "rtl" : "ltr"}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", minHeight: "70vh", padding: 24,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', var(--mwps-font, inherit), sans-serif",
        }}>
        <div style={{
          maxWidth: 420, width: "100%", textAlign: "center",
          background: "#fff", borderRadius: 24, padding: "40px 28px",
          boxShadow: "0 12px 40px rgba(20,22,40,0.12)",
        }}>
          <div style={{
            width: 64, height: 64, margin: "0 auto 18px", borderRadius: 18,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #3a3a8c, #26264f)", fontSize: 30,
          }}>🔒</div>

          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1c1c22", margin: "0 0 12px" }}>
            {rtx("title", lang)}
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: "#8a8a90", margin: "0 0 26px" }}>
            {rtx("message", lang)}
          </p>

          <button
            onClick={goRelogin}
            style={{
              width: "100%", padding: 15, border: "none", borderRadius: 14,
              background: "linear-gradient(135deg, #2f2f6b, #26264f)", color: "#fff",
              fontSize: 16, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 6px 16px rgba(38,38,79,0.28)",
            }}
          >
            {rtx("login", lang)}
          </button>

          <button
            onClick={() => window.location.reload()}
            style={{
              display: "block", margin: "12px auto 0", background: "transparent",
              border: "none", color: "#9a9da6", fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            {rtx("reload", lang)}
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // ЕСТЬ СЕССИЯ — обычный дашборд
  // ══════════════════════════════════════════════════════════
  return (
    <div className="cd-root" dir={isRTL ? "rtl" : "ltr"}>

      {/* ══ DESKTOP SIDEBAR ══════════════════════════════════ */}
      <aside className="cd-sidebar">
        <div className="cd-sidebar__logo">
          <div className="cd-sidebar__logo-icon">🚀</div>
          <span className="cd-sidebar__logo-text">ZmanUp</span>
        </div>

        <nav className="cd-sidebar__nav">
          {TABS.map(({ id, Icon, labelKey, labelRu }) => (
            <button
              key={id}
              className={`cd-sidebar__item ${activeTab === id ? "cd-sidebar__item--on" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <span className="cd-sidebar__item-icon"><Icon size={17} /></span>
              <span className="cd-sidebar__item-label">{t(labelKey) || labelRu}</span>
            </button>
          ))}
        </nav>

        <div className="cd-sidebar__footer">
          <div className="cd-sidebar__user" onClick={() => setActiveTab("profile")}>
            {photoUrl
              ? <img className="cd-sidebar__user-ava-img" src={photoUrl} alt={fullName} />
              : <div className="cd-sidebar__user-ava">{initials}</div>
            }
            <div>
              <div className="cd-sidebar__user-name">{fullName || t("my_profile") || "Профиль"}</div>
            </div>
          </div>

          {masterLink && (
            <button className="cd-switch-to-master-btn" onClick={handleSwitchToMaster}>
              <span>👩‍🎓</span>
              <span>{t("switch_to_master") || "Мой кабинет мастера"}</span>
            </button>
          )}
        </div>
      </aside>

      {/* ══ MAIN AREA ════════════════════════════════════════ */}
      <div className="cd-main">
        <header className="cd-topbar">
          <span className="cd-topbar__title">{activeLabel}</span>

          <div className="cd-topbar__actions">
            <button className="cd-topbar__bell" aria-label="notifications">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b0c4c4" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </button>

            <LanguageScreen />
          </div>
        </header>



        {showInstallBanner && (
          <InstallSlider
            lang={lang}
            canInstall={canInstall}
            isIos={isIos}
            onInstall={install}
            onDismiss={dismissBanner}
          />
        )}

        <div className="cd-page" key={activeTab}>
          {activeTab === "appointments" && <AppointmentsList />}
          {activeTab === "profile" && <ClientProfile />}
          {activeTab === "favorites" && <FavoriteMasters />}
          {activeTab === "settings" && <SettingsPanel />}
        </div>
      </div>

      {/* ══ MOBILE BOTTOM TAB BAR ═══════════════════════════ */}
      <nav className="cd-tabbar">
        {TABS.map(({ id, Icon, labelKey, labelRu }) => {
          const on = activeTab === id;
          return (
            <button key={id} className={`cd-tab ${on ? "cd-tab--on" : ""}`} onClick={() => setActiveTab(id)}>
              <span className={`cd-tab__icon ${on ? "cd-tab__icon--on" : ""}`}><Icon size={22} /></span>
              <span className="cd-tab__label">{t(labelKey) || labelRu}</span>
            </button>
          );
        })}

        {masterLink && (
          <button className="cd-tab cd-tab--master" onClick={handleSwitchToMaster}>
            <span className="cd-tab__icon">👩‍🎓</span>
            <span className="cd-tab__label">{t("switch_to_master") || "Мастер"}</span>
          </button>
        )}
      </nav>
    </div>
  );
}